import React, { useState, useRef, useEffect } from 'react';
import { CCard, CCardBody, CCardHeader } from '@coreui/react';
import {
    Text,
    Image,
    Flex,
    useColorModeValue
} from "@chakra-ui/react";
import textBannerImage from "./assets/img/promotions/group-banner-default.png";
import { useLinepros } from "./useLinepros";
import { Helmet } from "react-helmet";

const faqsData = [
    {
        title: 'What deposit methods do we use?',
        content: `
            Through the cashier section you can choose your favorite deposit method available.<br/>
            <strong>VISA/MC, CRYPTO, MG, PAYPAL</strong>
        `,
    },
    {
        title: 'Minimum Deposit & Credit Card Deposits',
        content: `
            <p><strong>Minimum deposit:</strong> $10 and carries a 1x playthrough</p>
            <p><strong>CC deposits:</strong> will carry 21 day hold</p>
        `,
    },
    {
        title: 'Crypto',
        content: `
            <strong>What currencies do we accept?</strong><br/>
            Bitcoin, Litecoin & Ether. All transactions are made in US dollars and Crypto for continuity purposes.
            However, all the funds in your account are in USD.
        `,
    },
    {
        title: 'Credit Cards',
        content: `
            <strong>Can I use the credit/debit card of someone I know to deposit funds in my account?</strong><br/>
            No, the card must belong to the account holder; no third-party deposits are allowed.
            Transactions processed using third party cards are deemed as high risk and will ban your account
            and personal information from future credit/debit card deposits.
        `,
    },
    {
        title: 'How to withdraw?',
        content: `
            Go to the cashier in your account and then select your preferred payout method by clicking on it,
            follow the simple instructions to complete the request.<br/>
            Minimum withdrawal amount: <strong>$100</strong><br/>
            Max per request: <strong>$5000</strong>
        `,
    },
    {
        title: 'How to use bonuses?',
        content: `
            Access your cashier and click on <strong>Bonuses</strong> to enter your redemption code and activate it.
            The allowed games will be displayed.<br/><br/>

            <strong>Deposit Bonuses Playthrough:</strong><br/>
            Calculated based on your deposit plus bonus, multiplied by the playthrough requirement of your bonus.<br/>
            Higher deposit bonuses for crypto deposits and lower playthrough.<br/>
            That’s right! Crypto deposits will grant you with the best bonus deals.<br/><br/>

            <strong>Do bonuses expire?</strong><br/>
            Yes, bonuses will expire after 30 days once redeemed.
        `,
    },
    {
        title: 'Welcome Freechips',
        content: `
            Welcome freechip winnings max cash out is subject to $100.
            Unless funding the account, there will be no max cashout on this bonus.
        `,
    },
    {
        title: 'Restricted regions?',
        content: `
            Due to governmental regulations our services are restricted for the following regions.
        `,
    },
    {
        title: 'Claims?',
        content: `
            In the event of any claim of game malfunction or system error, the casino requires at least 48 business hours
            in order to review your request. Requests will be attended as they come.
        `,
    },
    {
        title: 'Refer a friend',
        content: `
            Terms & Conditions Apply:<br/>
            Referrer and friend must each deposit a minimum of $100.<br/>
            Free Bet will be credited within 24 hours of qualifying deposits.<br/>
            Free Bet expires in 7 days.<br/>
            Abuse of promotion may result in disqualification.<br/>
        `,
    },
];

const AccordionItem = ({ title, content, isOpen, onToggle }) => {
    const ref = useRef(null);
    const [maxHeight, setMaxHeight] = useState('0px');

    useEffect(() => {
        if (isOpen && ref.current) {
            setMaxHeight(`${ref.current.scrollHeight + 20}px`); // +20 para padding extra
        } else {
            setMaxHeight('0px');
        }
    }, [isOpen]);

    return (
        <CCard className="border-0 rounded-0 mb-2" style={{ marginBottom: '2rem' }}>
            <CCardHeader
                className="fw-semibold"
                style={{
                    backgroundColor: '#90cdf4',
                    color: 'black',
                    cursor: 'pointer',
                    borderRadius: '4px 4px 0 0',
                }}
                onClick={onToggle}
            >
                &nbsp;&nbsp;{title}
            </CCardHeader>

            <CCardBody
                style={{
                    maxHeight: maxHeight,
                    overflow: 'hidden',
                    transition: 'max-height 0.4s ease',
                    backgroundColor: '#1f2430',
                    color: 'white',
                    padding: isOpen ? '1rem' : '0',
                }}
            >
                <div ref={ref} dangerouslySetInnerHTML={{ __html: content }} />
            </CCardBody>
        </CCard>
    );
};

const FAQS = () => {
    const { trackHeight } = useLinepros();

    useEffect(() => {
        trackHeight();
    }, [trackHeight]);

    const [openIndex, setOpenIndex] = useState(0);

    const handleToggle = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const bg = useColorModeValue("white", "gray.800");
    const textColor = useColorModeValue("gray.800", "white");
    const tabBg = useColorModeValue("gray.100", "gray.700");

    return (
        <>
            <Helmet>
                {/* GTM en <head> */}
                <script>
                    {`
            (function(w,d,s,l,i){w[l]=w[l]||[];
              w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
              var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
              j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
              f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-K32LWXGQ');
          `}
                </script>
            </Helmet>

            {/* GTM después del <body> (noscript) */}
            <noscript>
                <iframe
                    src="https://www.googletagmanager.com/ns.html?id=GTM-K32LWXGQ"
                    height="0"
                    width="0"
                    style={{ display: "none", visibility: "hidden" }}
                />
            </noscript>
            <Flex
                flexDirection="column"
                pt={{ base: "120px", md: "65px" }}
                px={{ base: 4, md: 8 }}
                bg={bg}
                color={textColor}
                minH="100vh"
            >
                <Flex
                    justify="space-between"
                    align="center"
                    mb="6"
                    height={{base:115,sm:20}}
                    backgroundColor={tabBg}
                    p={4}
                >
                    {/* Text on the left */}
                    <Text fontSize={{ base: 'xl',sm:"md", md: '2xl', lg: '3xl' }} fontWeight="bold" color={textColor}>
                        Welcome to our FAQ section!
                    </Text>

                    {/* Text Banner Image */}
                    <Image
                        src={textBannerImage}
                        alt="Banner"
                        maxWidth={{ base: "45%", sm: "60%", md: "45%",lg:"25%" }}
                        height="auto"
                    />
                </Flex>
                <style>
                    {`
                  @media (max-width: 767px) {
                    .mx-auto {
                      grid-template-columns: 1fr !important;
                    }
                  }
                `}
                </style>
                <p>
                    Here you'll find answers to the most common questions about our services and how to get the most out of your experience. If you don’t see your question here, feel free to contact our support team—we’re always happy to help!
                </p>
                <br />
                <p>
                    Please click on each question to see the answer.
                </p>
                <br />
                <div className="mx-auto" style={{ maxWidth: '768px' }}>
                    {faqsData.map((faq, i) => (
                        <AccordionItem
                            key={i}
                            title={faq.title}
                            content={faq.content}
                            isOpen={openIndex === i}
                            onToggle={() => handleToggle(i)}
                        />
                    ))}
                </div>
            </Flex>
        </>

    );
};

export default FAQS;
